import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {Input} from '@cohort/wallet/components/forms/Input';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {utils} from 'ethers';
import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface PolygonTransferProps {
  ownership: OwnershipWDto;
  onTransferSuccess: () => void;
}

function isAddressValid(address: string): boolean {
  try {
    utils.getAddress(address);
    return true;
    // eslint-disable-next-line no-empty
  } catch {}
  return false;
}

const PolygonTransfer: React.FC<PolygonTransferProps> = ({ownership, onTransferSuccess}) => {
  const {t} = useTranslation('components', {keyPrefix: 'space.nftTransfer.polygonTransfer'});

  const [address, setAddress] = useState('');
  const addressIsValid = useMemo(() => isAddressValid(address), [address]);
  const [isInPreview, setIsInPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {accentColor, hasDarkBg} = useThemeContext();
  const requestOwnershipTransfer = useApi(OwnershipsApi.requestOwnershipTransfer);

  useEffect(() => {
    setAddress('');
    setIsInPreview(false);
    setIsLoading(false);
  }, [ownership]);

  const onCancelPreviewClick = useCallback(() => {
    setIsInPreview(false);
  }, []);

  const {mutate: requestTransfer} = useCohortMutation({
    mutationFn: async () =>
      requestOwnershipTransfer(ownership.id, {
        recipientAddress: address,
      }),
    onSuccess: () => onTransferSuccess(),
  });

  const onSubmit = useCallback(() => {
    if (isInPreview) {
      requestTransfer();
    } else {
      setIsInPreview(true);
    }
  }, [isInPreview, requestTransfer]);

  let footer: JSX.Element;
  if (isInPreview) {
    footer = (
      <Fragment>
        <p className="mb-3 mt-1 text-orange-500">{t('transferFormWarning')}</p>
        <div className="w-full md:flex">
          <div className="w-full md:mr-2 md:w-fit">
            <SecondaryButton
              testId="polygon-transfer-back-button"
              type={isLoading ? 'submit' : 'button'}
              disabled={!addressIsValid}
              text={t('transferFormBack')}
              onClick={onCancelPreviewClick}
              className="h-full w-full"
              tracking={{namespace: 'digitalAssets.transfer.polygon.back'}}
            />
          </div>
          <div className="mt-3 w-full md:ml-2 md:mt-0 md:w-fit">
            <PrimaryButton
              testId="polygon-transfer-submit-button"
              type={isLoading ? 'submit' : 'button'}
              text={t('transferFormSubmit')}
              className="w-full"
              onClick={onSubmit}
              tracking={{namespace: 'digitalAssets.transfer.polygon.transfer'}}
            />
          </div>
        </div>
      </Fragment>
    );
  } else {
    const previewButton = (
      <PrimaryButton
        testId="polygon-transfer-preview-button"
        type={isLoading ? 'submit' : 'button'}
        text={t('transferFormPreview')}
        className="mt-10 w-full md:w-fit"
        onClick={onSubmit}
        disabled={!addressIsValid || address.length === 0}
        tracking={{namespace: 'digitalAssets.transfer.polygon.preview'}}
      />
    );

    if (address.length > 0) {
      const message = addressIsValid ? (
        <p className="mt-2 text-green-600">{t('transferFormAddressValid')}</p>
      ) : (
        <p className="mt-2 text-sm text-red-600">{t('transferFormAddressInvalid')}</p>
      );
      footer = (
        <Fragment>
          {message}
          {previewButton}
        </Fragment>
      );
    } else {
      footer = previewButton;
    }
  }

  if (ownership.digitalAsset.nft === null) {
    return <Fragment />;
  }

  return (
    <div className="flex flex-col">
      <label
        className={classNames('mb-1 w-full text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}
      >
        {t('transferFormFrom')}
      </label>
      <Input
        testId="polygon-transfer-input-sender-address"
        type="text"
        name="Sender wallet address"
        placeholder={t('transferFormSenderValue', {
          blockchainName: ownership.digitalAsset.nft.blockchainName,
        })}
        disabled
        accentColor={accentColor}
        hasDarkBg={hasDarkBg}
        classes="hover:cursor-not-allowed"
      />

      <div className="mt-4">
        <label className={classNames('w-full text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}>
          {t('transferFormTo', {blockchain: ownership.digitalAsset.nft.blockchainName})}
        </label>

        <Input
          testId="polygon-transfer-input-recipient-address"
          type="text"
          name="Recipient wallet address"
          accentColor={accentColor}
          placeholder="0x…"
          hasDarkBg={hasDarkBg}
          onChange={e => setAddress(e.target.value)}
          disabled={isInPreview}
        />
      </div>
      {footer}
    </div>
  );
};

export default PolygonTransfer;
